import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Card from 'pages/HehmDevice/HehmDashboard/Card'
import { HehmDeviceEquipment } from 'types/HehmDevice'
import { LabelAndValue } from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/LabelAndValue'
import { isValidNumericInput, mapToDropdownOptions, getErrorMessage } from 'helpers/formatters'
import ConfigDropdown from 'pages/Device/SystemConfigPanel/ConfigDropdown'
import {
  HEHM_OUTDOOR_UNIT_TYPE,
  HEHM_STAGES,
  HEHM_OUTDOOR_REFRIGERANT_TYPE
} from 'helpers/reportFriendlyNames'
import ConfigInput from 'pages/Device/SystemConfigPanel/ConfigInput'
import ConfigTextInput from './ConfigTextInput'
import NoDataValue from '../NoDataValue'

type Props = {
  hehmDeviceEquipment: HehmDeviceEquipment | null
  isEditing: boolean
  onChangeCallback: (outdoor: Partial<HehmDeviceEquipment['outdoor']>) => void
  onValidationChange?: (hasErrors: boolean) => void
}

const OutdoorUnitCard = ({ hehmDeviceEquipment, isEditing, onChangeCallback, onValidationChange }: Props) => {
  const { outdoor } = hehmDeviceEquipment || {}

  const [unitType, setUnitType] = useState<string | null>(outdoor?.type ?? null)
  const [stages, setStages] = useState<number | string | null>(outdoor?.stages ?? 'null')
  const [refrigerantType, setRefrigerantType] = useState<string | null>(outdoor?.refrigerantType ?? null)
  const [tonnage, setTonnage] = useState<number | null>(outdoor?.tonnage ?? null)
  const [modelNumber, setModelNumber] = useState(outdoor?.modelNumber ?? '')
  const [serialNumber, setSerialNumber] = useState(outdoor?.serialNumber ?? '')
  const [manufacturer, setManufacturer] = useState(outdoor?.manufacturer ?? '')

  useEffect(() => {
    if (!isEditing) {
      setUnitType(outdoor?.type ?? null)
      setStages(outdoor?.stages ?? 'null')
      setRefrigerantType(outdoor?.refrigerantType ?? null)
      setTonnage(outdoor?.tonnage ?? null)
      setModelNumber(outdoor?.modelNumber ?? '')
      setSerialNumber(outdoor?.serialNumber ?? '')
      setManufacturer(outdoor?.manufacturer ?? '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hehmDeviceEquipment, isEditing])

  useEffect(() => {
    onValidationChange?.(!unitType || !stages || !refrigerantType)
  }, [unitType, stages, refrigerantType, onValidationChange])

  return (
    <Card title="Outdoor Unit" icon="/img/outdoor_unit.svg">
      <S.CardContent>
        <S.Column>
          <LabelAndValue
            label="Unit Type"
            isRequired
            value={
              !isEditing && !unitType ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing}
                  value={unitType}
                  options={mapToDropdownOptions(HEHM_OUTDOOR_UNIT_TYPE)}
                  onChangeCallback={(value) => {
                    setUnitType(value as string | null)
                    onChangeCallback({ type: value as string | null })
                  }}
                  errorMessage={getErrorMessage('Unit Type', unitType)}
                />
              )
            }
          />
          <LabelAndValue
            label="Unit Stages"
            isRequired
            value={
              !isEditing && !stages ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing}
                  value={stages}
                  options={mapToDropdownOptions(HEHM_STAGES)}
                  onChangeCallback={(value) => {
                    setStages(value as number | string | null)
                    onChangeCallback({ stages: value === 'null' ? null : value as number | string | null })
                  }}
                  errorMessage={getErrorMessage('Unit Stages', stages)}
                />
              )
            }
          />
          <LabelAndValue
            label="Unit Tonnage"
            value={
              !isEditing && !tonnage ? (
                <NoDataValue />
              ) : (
                <ConfigInput
                  isEditing={isEditing}
                  value={tonnage}
                  onChangeCallback={(value) => {
                    if (value === null || isValidNumericInput(String(value), 3)) {
                      setTonnage(value as number | null)
                      onChangeCallback({ tonnage: value as number | null })
                    }
                  }}
                />
              )
            }
          />
          <LabelAndValue
            label="Refrigerant Type"
            isRequired
            value={
              !isEditing && !refrigerantType ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing}
                  value={refrigerantType}
                  options={mapToDropdownOptions(HEHM_OUTDOOR_REFRIGERANT_TYPE)}
                  onChangeCallback={(value) => {
                    setRefrigerantType(value as string | null)
                    onChangeCallback({ refrigerantType: value as string | null })
                  }}
                  errorMessage={getErrorMessage('Refrigerant Type', refrigerantType)}
                />
              )
            }
          />
        </S.Column>

        <S.Column>
          <LabelAndValue
            label="Model Number"
            value={
              !isEditing && !modelNumber ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={modelNumber}
                  errorMessage={modelNumber.length > 50 ? 'Maximum 50 characters' : undefined}
                  onChangeCallback={(value) => {
                    setModelNumber(value)
                    onChangeCallback({ modelNumber: value || null })
                  }}
                />
              )
            }
          />
          <LabelAndValue
            label="Serial Number"
            value={
              !isEditing && !serialNumber ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={serialNumber}
                  errorMessage={serialNumber.length > 50 ? 'Maximum 50 characters' : undefined}
                  onChangeCallback={(value) => {
                    setSerialNumber(value)
                    onChangeCallback({ serialNumber: value || null })
                  }}
                />
              )
            }
          />
          <LabelAndValue
            label="Manufacturer"
            value={
              !isEditing && !manufacturer ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={manufacturer}
                  maxLength={50}
                  onChangeCallback={(value) => {
                    setManufacturer(value)
                    onChangeCallback({ manufacturer: value || null })
                  }}
                />
              )
            }
          />
        </S.Column>
      </S.CardContent>
    </Card>
  )
}

export default OutdoorUnitCard

const S = {
  CardContent: styled.div`
    display: flex;
    gap: 25px;
  `,
  Column: styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;
  `
}
